import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Button,
  Skeleton,
  useColorModeValue,
  Text,
  Collapse,
  Alert,
  AlertIcon,
  chakra,
  Input,
} from "@chakra-ui/react";
import {
  Column,
  FilterDefinition,
  Row,
  useTable,
  UseTableParams,
} from "hooks/useTable";
import Card from "components/card/Card";
import { scrollToTop } from "utils";
import Filter from "components/filters";
import Pagination from "components/table/pagination";

const RouterChakraLink = chakra(Link);

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

type HeaderButton = React.ReactNode;

interface TableProps<T> {
  columns: Column<T>[];
  rowConfig?: Row<T>;
  fetchData: UseTableParams<T>["fetchData"];
  uniqueKey: keyof T;
  title?: string;
  filters?: FilterDefinition[];
  shouldSearch?: boolean;
  searchPlaceHolder?: string;
  buttons?: HeaderButton[];
  children?: ReactNode;
}

function GamelightTable<T>({
  columns,
  fetchData,
  uniqueKey,
  title,
  filters,
  rowConfig,
  shouldSearch = false,
  searchPlaceHolder,
  buttons,
  children,
}: TableProps<T>) {
  const history = useHistory();
  const searchParams = useQuery();
  const [pageSize, setPageSize] = useState<number>(
    parseInt(searchParams.get("pageSize") || "50")
  );
  const [page, setPage] = useState<number>(
    parseInt(searchParams.get("page") || "1")
  );
  const [searchTerm, setSearchTerm] = useState<string>(
    searchParams.get("searchTerm") || ""
  );

  const initialFilters = filters?.reduce((acc, curr) => {
    acc[curr.filterKey] = searchParams.get(curr.filterKey) || "";
    return acc;
  }, {} as Record<string, any>);

  const updateURLParams = useCallback(
    (
      page: number,
      pageSize: number,
      filters: Record<string, any>,
      searchTerm: string
    ) => {
      const params = new URLSearchParams();
      params.set("page", page.toString());
      params.set("pageSize", pageSize.toString());
      searchTerm && params.set("searchTerm", searchTerm);
      for (const key in filters) {
        if (filters[key] !== "") {
          params.set(key, filters[key]);
        }
      }
      history.push({ search: params.toString() });
    },
    [history]
  );

  const {
    data: allData,
    loading,
    totalRecords,
    error,
    updateFilter,
    updateItem,
    filters: FILTERS,
  } = useTable<T>({
    columns,
    fetchData,
    pageSize: !shouldSearch ? pageSize : 10000,
    page: !shouldSearch ? page : 1,
    filtersConfig: filters,
    initialFilters,
  });

  const filteredData = useMemo(() => {
    if (!searchTerm || !shouldSearch) return allData;

    return allData.filter((item: any) => {
      const uniqueKeyString = String(item[uniqueKey]).toLowerCase();
      const searchTermLower = searchTerm.toLowerCase();
      return (
        uniqueKeyString.includes(searchTermLower) ||
        Object.values(item).some(
          (value) =>
            typeof value === "string" &&
            value.toLowerCase().includes(searchTermLower)
        )
      );
    });
  }, [allData, searchTerm, uniqueKey, shouldSearch]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, page, pageSize]);

  const totalPages = shouldSearch
    ? Math.ceil(filteredData.length / pageSize)
    : Math.ceil(totalRecords / pageSize);

  useEffect(() => {
    updateURLParams(page, pageSize, FILTERS, searchTerm);
  }, [FILTERS, page, pageSize, searchTerm, updateURLParams]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    updateURLParams(newPage, pageSize, FILTERS, searchTerm);
    scrollToTop();
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
    updateURLParams(1, newSize, FILTERS, searchTerm);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
    updateURLParams(1, pageSize, FILTERS, e.target.value);
  };

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Flex flexDir={"column"}>
      <Collapse in={!!error}>
        <Flex mb="10px">
          <Alert status="error" borderRadius={8}>
            <AlertIcon />
            {error?.message}
          </Alert>
        </Flex>
      </Collapse>
      <Flex flexDir={"column"} alignItems="flex-start" gap={4}>
        {filters && (
          <Flex flex={1} flexDir="row" alignItems={"flex-start"} gap={4}>
            {filters.map((filter, index) => {
              return (
                <Filter
                  updateFilter={(key, value) => {
                    updateFilter(key, value);
                    setPage(1);
                  }}
                  key={(filter.filterKey as string) + index}
                  {...filter}
                  defaultValue={
                    initialFilters?.[filter.filterKey] ??
                    filter.defaultValue ??
                    ""
                  }
                />
              );
            })}
          </Flex>
        )}
        <Card
          flexDirection="column"
          flex={4}
          px="0px"
          overflowX={{ sm: "scroll", lg: "scroll" }}
        >
          <Flex px="25px" justify="space-between" mb="10px" align="center">
            <Flex align={"center"} gap={4}>
              <Text
                color={textColor}
                fontSize="22px"
                fontWeight="700"
                lineHeight="100%"
              >
                {title}
              </Text>
              {shouldSearch && (
                <Input
                  placeholder={searchPlaceHolder ?? "Search..."}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              )}
            </Flex>
            <Flex gap={2} alignItems="center">
             <Flex paddingRight={4}>
               {
                 // display buttons
                 buttons?.map((button, index) => (
                   <Flex key={`table-header-button-${index}`}>{button}</Flex>
                 ))
               }
             </Flex>
             {/* <Flex
               gap={2}
               alignItems="center"
               borderLeftWidth={buttons ? 1 : 0}
               borderLeftColor="gray.200"
               paddingLeft={4}
             >
               <Text fontWeight={"medium"} color={textColor}>
                 Page Size:
               </Text>
               <Flex gap={2}>
                 <Button
                   borderRadius={8}
                   onClick={() => handlePageSizeChange(25)}
                   backgroundColor={pageSize === 25 ? "purple.50" : "white"}
                 >
                   25
                 </Button>
                 <Button
                   borderRadius={8}
                   onClick={() => handlePageSizeChange(50)}
                   backgroundColor={pageSize === 50 ? "purple.50" : "white"}
                 >
                   50
                 </Button>
               </Flex>
             </Flex> */}
            </Flex>
          </Flex>
          <Table mb="24px">
            <Thead>
              <Tr>
                {columns.map((column) => (
                  <Th key={String(column.columnKey)} borderColor={borderColor}>
                    <Flex justify="space-between" align="center">
                      {column.header}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {(
                shouldSearch
                  ? paginatedData.length === 0 && loading
                  : allData.length === 0 && loading
              ) ? (
                <>
                  {
                    // display skeleton
                    Array.from({ length: pageSize }).map((_, index) => (
                      <Tr key={index}>
                        {columns.map((column) => (
                          <Td key={String(column.columnKey)}>
                            <Skeleton isLoaded={!loading} height="20px" />
                          </Td>
                        ))}
                      </Tr>
                    ))
                  }
                </>
              ) : (
                  shouldSearch
                    ? paginatedData.length === 0 && !loading
                    : allData.length === 0 && !loading
                ) ? (
                <>
                  <Tr>
                    <Td colSpan={columns.length} textAlign="center">
                      No data found
                    </Td>
                  </Tr>
                </>
              ) : (
                (shouldSearch ? paginatedData : allData).map((row, rowIndex) =>
                  rowConfig?.href ? (
                    <Tr
                      {...(rowIndex % 2 === 0
                        ? { backgroundColor: "gray.50" }
                        : {})}
                      {...(rowConfig?.rowStyle ? rowConfig.rowStyle(row) : {})}
                      {...(rowConfig?.rowStyle ? rowConfig.rowStyle(row) : {})}
                    >
                      <RouterChakraLink
                        display="contents"
                        to={rowConfig.href(row)}
                      >
                        {columns.map((column) => (
                          <Td
                            key={
                              (row[uniqueKey] as React.Key) +
                              String(column.columnKey)
                            }
                          >
                            <Skeleton isLoaded={!loading}>
                              {column.render ? (
                                column.render(row, updateItem)
                              ) : (
                                <Text
                                  fontSize="sm"
                                  fontWeight="500"
                                  color={textColor}
                                >
                                  {row[column.columnKey] as ReactNode}
                                </Text>
                              )}
                            </Skeleton>
                          </Td>
                        ))}
                      </RouterChakraLink>
                    </Tr>
                  ) : rowConfig?.onClick ? (
                    <Tr
                      {...(rowIndex % 2 === 0
                        ? { backgroundColor: "gray.50" }
                        : {})}
                      {...(rowConfig?.rowStyle ? rowConfig.rowStyle(row) : {})}
                      {...(rowConfig?.rowStyle ? rowConfig.rowStyle(row) : {})}
                      onClick={() => rowConfig.onClick(row)}
                    >
                      {columns.map((column) => (
                        <Td
                          key={
                            (row[uniqueKey] as React.Key) +
                            String(column.columnKey)
                          }
                        >
                          <Skeleton isLoaded={!loading}>
                            {column.render ? (
                              column.render(row, updateItem)
                            ) : (
                              <Text
                                fontSize="sm"
                                fontWeight="500"
                                color={textColor}
                              >
                                {row[column.columnKey] as ReactNode}
                              </Text>
                            )}
                          </Skeleton>
                        </Td>
                      ))}
                    </Tr>
                  ) : (
                    <Tr
                      {...(rowIndex % 2 === 0
                        ? { backgroundColor: "gray.50" }
                        : {})}
                      {...(rowConfig?.rowStyle ? rowConfig.rowStyle(row) : {})}
                    >
                      {columns.map((column) => (
                        <Td
                          key={
                            (row[uniqueKey] as React.Key) +
                            String(column.columnKey)
                          }
                        >
                          <Skeleton isLoaded={!loading}>
                            {column.render ? (
                              column.render(row, updateItem)
                            ) : (
                              <Text fontSize="sm" fontWeight="500">
                                {row[column.columnKey] as ReactNode}
                              </Text>
                            )}
                          </Skeleton>
                        </Td>
                      ))}
                    </Tr>
                  )
                )
              )}
            </Tbody>
          </Table>
          <Pagination
            handlePageChange={handlePageChange}
            page={page}
            totalPages={totalPages}
          />
        </Card>
      </Flex>
      {children}
    </Flex>
  );
}

export default GamelightTable;
